import React, { useRef, useState } from 'react'
import {
  Button,
  Divider,
  Dropdown,
  Empty,
  Icons,
  Input,
  Menu,
  Space,
} from '@pankod/refine-antd'
import { useInvalidate, useOne } from '@pankod/refine-core'
import clsx from 'clsx'

import { useProgramStore } from '../../../store'
import { filterMenuItemsRecursive } from '../utils'
import { useBlueprintConfiguration } from './sections/blueprint'
import { useCertificateConfiguration } from './sections/certificates'
import { useFeedbackConfiguration } from './sections/feedback'
import { useGradingConfiguration } from './sections/grading'
import { useModuleConfiguration } from './sections/modules'
import { useOtherConfiguration } from './sections/other'
import { useParticipantConfiguration } from './sections/participants'
import { useProgramToolsConfiguration } from './sections/program-tools'
import { useSelfEnrollmentConfiguration } from './sections/self-enrollment'
import { useTimeTrackerConfiguration } from './sections/time-tracker'

import type {
  TConfigurationButtonProps,
  TConfigurationMenuItems,
  TConfigurationSectionContext,
} from './types'
import type { InputRef } from 'antd'

const ConfigurationButton = ({
  className,
  disabled,
  program,
  programId,
  refetchProgramDetail,
}: TConfigurationButtonProps) => {
  const isOwner = useProgramStore(({ isOwner }) => isOwner)
  const invalidate = useInvalidate()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputSearchRef = useRef<InputRef>(null)
  const { data: hideAddParticipantData } = useOne<{
    data: { isActive: boolean }
  }>({
    dataProviderName: 'lms',
    id: 'flag',
    resource: `programs/${programId}/hide_add_participant`,
  })
  const hideAddParticipantActive =
    hideAddParticipantData?.data?.data?.isActive || false

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setTimeout(() => inputSearchRef.current?.focus(), 100)
    } else {
      setSearch('')
    }
    setOpen(nextOpen)
  }

  const runAndClose = async (
    callback: () => void | Promise<unknown>,
  ): Promise<void> => {
    await callback()
    handleOpenChange(false)
  }

  const invalidateProgramDetail = () =>
    invalidate({
      invalidates: ['detail'],
      dataProviderName: 'lms',
      resource: 'programs',
      id: programId,
    })
  const refreshProgramDetail = async () => {
    if (refetchProgramDetail) {
      await refetchProgramDetail()
      return
    }

    await invalidateProgramDetail()
  }

  const context: TConfigurationSectionContext = {
    closeMenu: () => handleOpenChange(false),
    hideAddParticipantActive,
    invalidateProgramDetail,
    isOwner,
    program,
    programId,
    refetchProgramDetail: refreshProgramDetail,
    runAndClose,
    search,
  }
  const sections = [
    useGradingConfiguration(context),
    useModuleConfiguration(context),
    useFeedbackConfiguration(context),
    useCertificateConfiguration(context),
    useParticipantConfiguration(context),
    useSelfEnrollmentConfiguration(context),
    useTimeTrackerConfiguration(context),
    useBlueprintConfiguration(context),
    useProgramToolsConfiguration(context),
    useOtherConfiguration(context),
  ]
  const menuItems = sections
    .flatMap((section) => section.menuItems)
    .filter(
      (item): item is Exclude<TConfigurationMenuItems[number], null> =>
        item !== null,
    )
    .toSorted((a, b) => {
      const aKey = String(a.key ?? '')
      const bKey = String(b.key ?? '')
      return aKey === 'Konfigurasi Lainnya' ? 1 : aKey.localeCompare(bKey)
    })
  const filteredMenuItems = filterMenuItemsRecursive(menuItems, search)

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={
          <div className="dropdown-search flex flex-col">
            <Input.Search
              placeholder="Cari menu konfigurasi"
              ref={inputSearchRef}
              autoFocus
              className="!w-[calc(100%_-_32px)] mt-3 mx-4"
              value={search}
              allowClear
              onChange={(event) => setSearch(event.target.value)}
            />
            <Divider className="!my-3 !mb-0" />
            {filteredMenuItems.length ? (
              <Menu
                className="flex flex-col w-fit overflow-auto max-h-80 gap-3 !pb-3 !px-1 !shadow-none"
                items={filteredMenuItems}
              />
            ) : (
              <Empty
                description="Tidak ada konfigurasi"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        }
        overlayClassName="!z-[1000] w-fit max-h-[525px]"
        disabled={disabled}
        visible={open}
        onVisibleChange={handleOpenChange}
      >
        <Button
          icon={<Icons.SettingOutlined className="mr-2" />}
          className={className}
        >
          <Space>
            Konfigurasi Program
            <Icons.DownOutlined
              className={clsx(
                open && '-rotate-180',
                !open && '!delay-100',
                '!transition-transform',
              )}
            />
          </Space>
        </Button>
      </Dropdown>
      {sections.map((section, index) => (
        <React.Fragment key={index}>{section.overlays}</React.Fragment>
      ))}
    </>
  )
}

export default ConfigurationButton
